import { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import OnPremAgentClient from '~/api/OnPremAgentClient/OnPremAgentClient'
import useOnPremAgentClient from '~/api/OnPremAgentClient/useOnPremAgentClient'
import { Dataset, PaginatedDatasetsResponse } from '~/api/onPremAgent/datasetsAPI'
import useOnPremAgentQuery from '~/api/onPremAgent/useOnPremAgentQuery'
import { FullPageError } from '~/components/Errors'
import Input from '~/components/Input'
import LoadingMessage from '~/components/LoadingMessage'
import PopupMenu from '~/components/PopupMenu'
import Toaster from '~/components/Toaster'
import { useDebounce } from '~/utils/hooks/useDebounce'
import DatasetBreadcrumbs from './DatasetBreadcrumbs'
import DatasetsTable, { getPlateBarcode } from './DatasetsTable'
import cs from './datasets_list.scss'
import PaginationControls, { DEFAULT_PAGE_SIZE } from './PaginationControls'

type DatasetsQueryParams = {
  page: number
  pageSize: number
  reloadNonce: number
  searchQuery: string
}

export default function DatasetsList() {
  const [searchQuery, setSearchQuery] = useState('')
  const [validateAllInProgress, setValidateAllInProgress] = useState(false)
  const [_retryAllInProgress, _setRetryAllInProgress] = useState(false)
  const [retryingDatasetUuid, setRetryingDatasetUuid] = useState<string | null>(null)
  const [reloadNonce, setReloadNonce] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const client = useOnPremAgentClient()
  const history = useHistory()

  const debouncedSearchQuery = useDebounce(searchQuery, 250) // 250ms debounce

  useEffect(() => {
    setPage(1)
  }, [debouncedSearchQuery])

  const apiParams = useMemo<DatasetsQueryParams>(
    () => ({
      page,
      pageSize,
      reloadNonce,
      searchQuery: debouncedSearchQuery,
    }),
    [page, pageSize, reloadNonce, debouncedSearchQuery],
  )

  let { loading, error, data } = useOnPremAgentQuery<
    DatasetsQueryParams,
    PaginatedDatasetsResponse
  >({
    backendQueryParams: {
      apiQuery: async (client: OnPremAgentClient, params: DatasetsQueryParams) =>
        client.datasets.listDatasets({
          page: params.page,
          pageSize: params.pageSize,
          plateName: params.searchQuery,
        }),
      apiParams,
    },
  })

  useEffect(() => {
    if (!data) return
    const totalPages = Math.max(1, Math.ceil(data.total / pageSize) || 1)
    if (page > totalPages) {
      setPage(totalPages)
    }
  }, [data, page, pageSize])

  const handleValidateAll = async () => {
    setValidateAllInProgress(true)
    try {
      Toaster.show({
        message: `Validating all pending and ready datasets.`,
        intent: 'primary',
      })
      const invalidDatasets = await client.datasets.validateAllDatasets()
      if (!invalidDatasets.length) {
        Toaster.show({
          message: 'All datasets validated successfully.',
          intent: 'success',
        })
      } else {
        const invalidDatasetNames = invalidDatasets
          .map(dataset => getPlateBarcode(dataset) || dataset.uuid)
          .join(', ')
        Toaster.show({
          message: `Some datasets were invalid and their statuses have been updated: ${invalidDatasetNames}`,
          intent: 'warning',
        })
        setReloadNonce(value => value + 1)
      }
    } catch (_e) {
      Toaster.show({
        message: 'Failed to trigger validation for datasets.',
        intent: 'danger',
      })
    } finally {
      setValidateAllInProgress(false)
    }
  }

  const handleRetryDataset = async (dataset: Dataset) => {
    setRetryingDatasetUuid(dataset.uuid)
    const datasetDisplayName = getPlateBarcode(dataset) || dataset.uuid
    try {
      await client.datasets.retryDataset(dataset.uuid)
      Toaster.show({
        message: `Retrying upload for ${datasetDisplayName}.`,
        intent: 'success',
      })
      setReloadNonce(value => value + 1)
    } catch (_e) {
      Toaster.show({
        message: `Failed to retry upload for ${datasetDisplayName}.`,
        intent: 'danger',
      })
    } finally {
      setRetryingDatasetUuid(null)
    }
  }

  const popupMenuOptions = [
    {
      label: 'Review Missed Datasets',
      action: () => history.push('/datasets/missed'),
    },
    {
      label: 'Validate Pending Datasets',
      action: handleValidateAll,
      disabled: validateAllInProgress,
    },
  ]

  const datasets = data?.results || []
  if (error) {
    return <FullPageError error={error} />
  }
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
  }
  return (
    <div className={cs.datasetsList}>
      <div className={cs.topBar}>
        <div className={cs.controls}>
          <div className={cs.breadcrumbsRow}>
            <DatasetBreadcrumbs />
            <PopupMenu
              options={popupMenuOptions}
              placement='bottom-end'
              className={cs.menu}
              minimal
            />
          </div>
          <div className={cs.spacer} />
          <Input
            className={cs.searchInput}
            inputClassName={cs.input}
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder='Search Plate Barcodes...'
          />
        </div>
      </div>
      {loading && (
        <LoadingMessage className={cs.loadingMessage} label={'Loading datasets...'} />
      )}
      {!loading && (
        <Content
          datasets={datasets}
          onRetryDataset={handleRetryDataset}
          retryingDatasetUuid={retryingDatasetUuid}
          page={page}
          pageSize={pageSize}
          total={data?.total || 0}
          onPageChange={setPage}
          onPageSizeChange={newPageSize => {
            setPageSize(newPageSize)
            setPage(1)
          }}
        />
      )}
    </div>
  )
}

function Content(props: {
  datasets: Dataset[]
  onRetryDataset: (dataset: Dataset) => void
  retryingDatasetUuid: string | null
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}) {
  const {
    datasets,
    onRetryDataset,
    retryingDatasetUuid,
    page,
    pageSize,
    total,
    onPageChange,
    onPageSizeChange,
  } = props
  return (
    <div className={cs.content}>
      <div className={cs.tableWrapper}>
        <DatasetsTable
          datasets={datasets}
          onRetryDataset={onRetryDataset}
          retryingDatasetUuid={retryingDatasetUuid}
        />
      </div>
      <PaginationControls
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  )
}
